import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { TriangleAlert } from "lucide-react";
import { useAuth } from "../../lib/auth";
import { cn } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import type { IncidentListSearch } from "../incidents/search";
import { useDashboardSummary } from "./api";
import {
  last30DaysCategorySearch,
  last7DaysSearch,
  missingPostmortemSearch,
  openIncidentsSearch,
  overdueFollowupSearch,
} from "./drilldown";
import { ErrorBanner } from "./parts";

/**
 * M-003 Dashboard (F-020) - strictly read-only: the KPI tile row for every
 * role, each tile a drilldown into the filtered list. The trend analyses of
 * [F-023] are the Trend-Dashboard's (trends-page.tsx), a view of its own;
 * whoever may see it (`dashboard.trends`) gets a pointer to it here.
 */

export function DashboardPage(): JSX.Element {
  const { t } = useTranslation();
  const { can } = useAuth();
  // F-023: the trend analyses are `dashboard.trends` ([F-027]).
  const isManager = can("dashboard.trends");

  const summary = useDashboardSummary();

  return (
    <div className="space-y-6">
      <h1 data-testid="dashboard_title" className="text-2xl font-semibold tracking-tight">
        {t("dashboard.title")}
      </h1>

      {/* KPI tile row (F-020) - all roles, every tile a drilldown link. */}
      {summary.isError ? (
        <ErrorBanner section="tiles" onRetry={() => void summary.refetch()} />
      ) : summary.isLoading || !summary.data ? (
        <div
          data-testid="dashboard_tiles-loading"
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5"
        >
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="h-32 animate-pulse rounded-xl border bg-muted" />
          ))}
        </div>
      ) : (
        <div
          data-testid="dashboard_tiles"
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5"
        >
          <StatTile
            tile="open"
            label={t("dashboard.tiles.open")}
            search={openIncidentsSearch()}
            value={summary.data.openBySeverity.reduce(
              (sum, item) => sum + item.count,
              0,
            )}
          >
            {summary.data.openBySeverity.length > 0 ? (
              <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-sm text-muted-foreground">
                {summary.data.openBySeverity.map((item, index) => (
                  <span key={item.severityId} className="inline-flex items-center gap-2">
                    {index > 0 ? <span aria-hidden="true">/</span> : null}
                    <Link
                      to="/incidents"
                      search={openIncidentsSearch(item.severityId)}
                      data-testid={`dashboard_severity-link_${item.severityId}`}
                      className="relative z-10 whitespace-nowrap underline-offset-4 hover:text-foreground hover:underline"
                    >
                      {item.count} {item.severityName}
                    </Link>
                  </span>
                ))}
              </div>
            ) : null}
          </StatTile>

          <StatTile
            tile="openPostmortems"
            label={t("dashboard.tiles.openPostmortems")}
            search={missingPostmortemSearch()}
            value={summary.data.resolvedWithoutPostmortem}
          />

          <StatTile
            tile="overdue"
            label={t("dashboard.tiles.overdue")}
            search={overdueFollowupSearch()}
            value={summary.data.overdueFollowupTasks}
            // Red + icon, never color alone (NF: identity not color-only).
            emphasis={summary.data.overdueFollowupTasks > 0}
          />

          <StatTile
            tile="last7"
            label={t("dashboard.tiles.last7")}
            search={last7DaysSearch()}
            value={summary.data.last7Days}
          />

          <StatTile
            tile="last30ByCategory"
            label={t("dashboard.tiles.last30ByCategory")}
            search={last30DaysCategorySearch(null)}
            value={summary.data.last30DaysByCategory.reduce(
              (sum, item) => sum + item.count,
              0,
            )}
          >
            {summary.data.last30DaysByCategory.length > 0 ? (
              <ul className="space-y-0.5 text-sm text-muted-foreground">
                {summary.data.last30DaysByCategory.slice(0, 4).map((item) => (
                  <li key={item.categoryId ?? "none"}>
                    <Link
                      to="/incidents"
                      search={last30DaysCategorySearch(item.categoryId)}
                      data-testid={`dashboard_category-link_${item.categoryId ?? "none"}`}
                      className="relative z-10 inline-flex w-full items-center justify-between gap-2 underline-offset-4 hover:text-foreground hover:underline"
                    >
                      <span className="truncate">
                        {item.categoryName ?? t("dashboard.tiles.noCategory")}
                      </span>
                      <span className="font-medium tabular-nums">
                        {item.count}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : null}
          </StatTile>
        </div>
      )}

      {/* F-023: the Trend-Dashboard is a view of its own (/trends); the
       * dashboard points managers to it. */}
      {isManager ? (
        <Card data-testid="dashboard_trends-link-card">
          <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
            <div className="space-y-0.5">
              <div className="text-sm font-medium">{t("trends.title")}</div>
              <p className="text-sm text-muted-foreground">{t("trends.teaser")}</p>
            </div>
            <Button asChild variant="outline" className="h-11 sm:h-9">
              <Link to="/trends" data-testid="dashboard_trends-link">
                {t("trends.open")}
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

type TileKey =
  | "open"
  | "openPostmortems"
  | "overdue"
  | "last7"
  | "last30ByCategory";

interface StatTileProps {
  tile: TileKey;
  label: string;
  search: IncidentListSearch;
  value: number;
  /** Overdue emphasis: red number + warning icon (never color alone). */
  emphasis?: boolean;
  children?: ReactNode;
}

/**
 * Stat tile: big number, small label, whole tile a drilldown link (AC-020)
 * via the stretched-link pattern; inner breakdown links sit above it (z-10).
 */
function StatTile({
  tile,
  label,
  search,
  value,
  emphasis = false,
  children,
}: StatTileProps): JSX.Element {
  return (
    <Card
      data-testid={`dashboard_tile_${tile}`}
      className="relative p-4 transition-colors hover:border-primary/60"
    >
      <Link
        to="/incidents"
        search={search}
        aria-label={label}
        data-testid={`dashboard_tile-link_${tile}`}
        className="absolute inset-0 rounded-xl focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      />
      <div className="text-sm text-muted-foreground">{label}</div>
      <div
        data-testid={`dashboard_tile-value_${tile}`}
        className={cn(
          "mt-1 flex items-center gap-2 text-3xl font-semibold tabular-nums",
          emphasis && "text-destructive-text",
        )}
      >
        {value}
        {emphasis ? (
          <TriangleAlert aria-hidden="true" className="h-5 w-5" />
        ) : null}
      </div>
      {children ? <div className="mt-2">{children}</div> : null}
    </Card>
  );
}
